import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FormatAnnotationInput,
  getHospitalFormatSetting,
  postFormatAnnotation,
} from '../../apis/formatSetting'

const STALE_MS = 30_000

/** 変更してから保存するまでの間。連続した操作を 1 行（1 スナップショット）に畳む。 */
export const SAVE_DEBOUNCE_MS = 1_000

export const formatSettingQueryKey = (fullHospitalCode: string | null) =>
  ['annotator', 'formatSetting', fullHospitalCode] as const

/** 機関の様式（現行値 + 編集ログの最新 + 選択肢）。同じ機関の別の紙を開いても同じ値が出る。 */
export const useHospitalFormatSettingQuery = (
  fullHospitalCode: string | null
) =>
  useQuery({
    queryKey: formatSettingQueryKey(fullHospitalCode),
    queryFn: async () => {
      if (!fullHospitalCode) return null
      const { data } = await getHospitalFormatSetting(fullHospitalCode)
      return data
    },
    enabled: !!fullHospitalCode,
    staleTime: STALE_MS,
  })

type Saver = {
  /** フォーム全体のスナップショットを渡す。debounce 後に 1 行追記する */
  save: (body: FormatAnnotationInput) => void
  /** 最後に保存できた行の created_at。まだ無ければ null */
  savedAt: string | null
  /** 保存に失敗した一言。成功したら消える */
  error: string
}

/**
 * 保存ボタンを置かず、変更のたびに debounce して POST する。
 *
 * - 同時に飛ばさない（in-flight 中の変更は pending に畳み、終わってからもう 1 行）。
 *   順番が入れ替わると古いスナップショットが「最新」になるため
 * - 失敗したら一言出して pending を捨てる（次の変更でまた保存する。勝手に再送しない）
 * - 保存できたら機関の GET と一覧を読み直す（現在値と「様式未設定」ラベルが変わる）
 * - unmount 時に pending が残っていれば待たずに送る（紙を切り替えた直後の変更を落とさない）
 */
export const useFormatAnnotationSaver = (
  ocrRequestEventId: string,
  fullHospitalCode: string | null,
  debounceMs: number = SAVE_DEBOUNCE_MS
): Saver => {
  const queryClient = useQueryClient()
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [error, setError] = useState('')
  const pending = useRef<FormatAnnotationInput | null>(null)
  const inFlight = useRef(false)
  const timer = useRef<number | undefined>(undefined)

  const flush = async () => {
    if (inFlight.current || !pending.current) return
    const body = pending.current
    pending.current = null
    inFlight.current = true
    try {
      const { data } = await postFormatAnnotation(ocrRequestEventId, body)
      setSavedAt(data.created_at)
      setError('')
      void queryClient.invalidateQueries({
        queryKey: formatSettingQueryKey(fullHospitalCode),
      })
      void queryClient.invalidateQueries({
        queryKey: ['annotator', 'prescriptions'],
      })
    } catch {
      setError('保存できませんでした')
    } finally {
      inFlight.current = false
      if (pending.current) void flush()
    }
  }

  const save = (body: FormatAnnotationInput) => {
    pending.current = body
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void flush(), debounceMs)
  }

  useEffect(
    () => () => {
      window.clearTimeout(timer.current)
      if (pending.current) void flush()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return { save, savedAt, error }
}
